"use client";

import { Button } from "@/components/ui/button";
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Carousel,
	CarouselContent,
	CarouselDots,
	CarouselItem,
} from "@/components/ui/carousel";
import type { BlogPostCard } from "@/lib/supabase/types";
import { cn, getBlogPostUrl } from "@/lib/utils";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import Link from "next/link";

export function PostsCarousel({
	posts,
	className,
}: { posts: BlogPostCard[]; className?: string }) {
	return (
		<Carousel
			plugins={[Autoplay({ delay: 4000 })]}
			className={cn(
				"relative flex flex-col bg-black/20 rounded-3xl",
				className,
			)}
		>
			<CarouselContent className="flex h-[250px] justify-between rounded-3xl">
				{posts.map((post) => (
					<CarouselItem key={`post-${post.slug}`}>
						<PostPreviewCard post={post} />
					</CarouselItem>
				))}
			</CarouselContent>
			<CarouselDots className="mb-4" />
		</Carousel>
	);
}

export function PostPreviewCard({
	post,
	className,
}: { post: BlogPostCard; className?: string }) {
	return (
		<Link
			href={getBlogPostUrl(post.slug)}
			className="rounded-3xl overflow-hidden"
		>
			<Card className="relative flex flex-col justify-between h-full bg-transparent rounded-none">
				<Image
					src={post.image || ""}
					alt={post.title}
					fill
					sizes="100vw"
					className={cn(
						"absolute z-[-1] h-full w-full rounded-3xl object-cover aspect-[16/9]",
					)}
				/>
				<CardHeader className="bg-black p-4 mask-to-t">
					<CardTitle className="text-white w-full text-2xl md:text-5xl font-bold">
						{post.title}
					</CardTitle>
					<CardDescription className="text-white w-full text-base md:text-lg">
						{post.description}
					</CardDescription>
				</CardHeader>
				<CardFooter className="w-full flex p-3 justify-end self-end">
					<Button
						type="submit"
						className="absolute bottom-3 right-3 hover:bg-primary text-background font-bold rounded-full shadow-xl bg-primary transition-all"
					>
						Read
					</Button>
				</CardFooter>
			</Card>
		</Link>
	);
}
